import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData, QueryKey } from '@tanstack/react-query';
import type { ApiError } from '../ApiError';

/**
 * Página de uma lista por cursor de instante (`since`/`next_since`) — a forma do
 * schema `Cursor` do contrato: repetir com `since = next_since`; `next_since`
 * nulo **ou igual ao `since`** pedido = fim. É o cursor padrão do hook; a lista de
 * decisões da tela usa o outro modo do contrato (`before`/`next_before`, D10), e o
 * passa em `next`.
 */
export interface PageByCursor {
  since: string;
  next_since: string | null;
}

export interface UsePagedCursorOptions<P extends PageByCursor, I> {
  /**
   * Chave da query **sem** o cursor: mudar qualquer filtro muda a chave, e a
   * lista recomeça do `since` inicial — é o `reset` ao mudar filtros.
   */
  queryKey: QueryKey;
  /** O cursor da primeira página (o `since` da janela, ou o `before` do modo decrescente). Entra na chave: mudou, recomeça. */
  since: string;
  /** Busca uma página a partir de um cursor. */
  fetchPage: (cursor: string) => Promise<P>;
  /** O cursor da página seguinte, ou `undefined` no fim. Padrão: `nextCursor` (`next_since`). */
  next?: (page: P) => string | undefined;
  /** Extrai os itens de uma página. */
  itens: (page: P) => I[];
  /**
   * Chave de deduplicação. O `since` é inclusivo, então o item da borda volta
   * repetido a cada página — de propósito (instantes empatam; cursor exclusivo
   * perderia decisão em silêncio). A dedup é aqui.
   */
  chave: (item: I) => string;
  /** Ordem de exibição dos itens acumulados; sem ela, ordem de chegada. */
  ordenar?: (a: I, b: I) => number;
  enabled?: boolean;
}

export interface PagedCursor<P extends PageByCursor, I> {
  /** Itens de todas as páginas, deduplicados e ordenados. */
  itens: I[];
  /** Páginas brutas, na ordem em que chegaram. */
  paginas: P[];
  /** A primeira página — onde vivem os metadados da fonte. */
  primeira: P | undefined;
  /** Há mais para carregar? `false` quando `next_since` é nulo ou igual ao `since`. */
  hasMore: boolean;
  carregarMais: () => void;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  error: ApiError | null;
  refetch: () => void;
}

/**
 * Lista por cursor de instante (T372): acumula páginas, deduplica pela chave,
 * diz quando acabou e recomeça quando os filtros mudam.
 *
 * Extraído de T16 (`useDecisions`) para T23 e o que mais vier a paginar por
 * `since`/`next_since`. A regra de fim é a do contrato — não é deste hook
 * decidir que "igual ao since" ainda é página.
 */
export function usePagedCursor<P extends PageByCursor, I>(
  opcoes: UsePagedCursorOptions<P, I>,
): PagedCursor<P, I> {
  const { queryKey, since, fetchPage, next = nextCursor, itens, chave, ordenar, enabled = true } = opcoes;

  const query = useInfiniteQuery<P, ApiError, InfiniteData<P>, QueryKey, string>({
    queryKey: [...queryKey, { since }],
    initialPageParam: since,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (last) => next(last),
    enabled,
    // A janela é relativa a "agora": cache velho aqui engana.
    staleTime: 0,
  });

  const pages = query.data?.pages ?? [];
  const acumulados = useMemo(() => {
    const byKey = new Map<string, I>();
    for (const page of pages) {
      for (const item of itens(page)) byKey.set(chave(item), item);
    }
    const lista = [...byKey.values()];
    return ordenar ? lista.sort(ordenar) : lista;
    // `itens`/`chave`/`ordenar` são funções puras dos chamadores; a dependência
    // que muda de verdade é `paginas`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  return {
    itens: acumulados,
    paginas: pages,
    primeira: pages[0],
    hasMore: query.hasNextPage,
    carregarMais: () => void query.fetchNextPage(),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    error: query.error ?? null,
    refetch: () => void query.refetch(),
  };
}

/** O cursor da página seguinte, ou `undefined` no fim da janela. */
export function nextCursor(page: PageByCursor): string | undefined {
  const next = page.next_since;
  if (!next || next === page.since) return undefined;
  return next;
}
